import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE, CHAKRAS } from "./shared";

const LINES = ["Bring a touch of", "balance & beauty", "into your", "everyday space."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16 lg:pt-32"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.6 }}
          className="hidden xl:block absolute left-6 top-1/2 -translate-y-1/2 -rotate-90 origin-left text-[10px] tracking-[0.5em] uppercase text-[#1B1915]/30 whitespace-nowrap"
          data-testid="hero-side-text"
        >
          Handcrafted — Seven Chakras — ELYNCOR
        </motion.p>
        <motion.div style={{ y: textY }} className="lg:col-span-6 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-xs tracking-[0.32em] uppercase text-[#8C7A56]/80 mb-8"
            data-testid="hero-eyebrow"
          >
            ELYNCOR ELEMENTS — Handcrafted Gemstone Décor
          </motion.p>

          <h1
            className="font-serif-display font-light tracking-tight leading-[1.06] text-[#1B1915] text-4xl sm:text-5xl lg:text-6xl"
            data-testid="hero-headline"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "italic text-[#C6A15B]" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.35 + i * 0.13, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-8 text-base sm:text-lg text-[#1A201C]/70 leading-relaxed max-w-md"
            data-testid="hero-subheading"
          >
            A handcrafted 7 Chakra Crystal Tree designed to add colour, character
            and a meaningful presence to your surroundings.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="#chakra-tree"
              className="px-7 py-3.5 rounded-full bg-[#1B1915] text-[#FDFBF7] text-[12px] tracking-[0.18em] uppercase hover:bg-[#2C2822] transition-colors duration-300"
              data-testid="hero-explore-button"
            >
              Explore the Tree
            </a>
            <a
              href="#enquire"
              className="px-7 py-3.5 rounded-full border border-[#1B1915]/30 text-[#1B1915] text-[12px] tracking-[0.18em] uppercase hover:border-[#1B1915] hover:bg-[#1B1915]/5 transition-colors duration-300"
              data-testid="hero-enquiry-button"
            >
              Send an Enquiry
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.45 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-[11px] tracking-[0.16em] uppercase text-[#1A201C]/55"
            data-testid="hero-badges"
          >
            <span>Hand-wired branches</span>
            <span>~300 gemstone chips</span>
            <span>Wooden-style base</span>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-6 relative flex justify-center" data-testid="hero-product-visual">
          <div className="spotlight-bg absolute inset-0 -m-10" />
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] sm:w-[540px] sm:h-[540px] rounded-full opacity-20 blur-3xl animate-[spin_46s_linear_infinite]"
            style={{
              background:
                "conic-gradient(from 0deg, #D9D5C7, #8B5CF6, #0284C7, #10B981, #EAB308, #F97316, #EF4444, #D9D5C7)",
            }}
          />
          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, delay: 0.5, ease: EASE }}
            className="relative"
          >
            <img
              src="/assets/tree.webp"
              alt="Handcrafted 7 Chakra Gemstone Tree by ELYNCOR ELEMENTS"
              className="relative z-10 w-full max-w-[340px] sm:max-w-[420px] lg:max-w-[460px] max-h-[75vh] object-contain drop-shadow-[0_45px_45px_rgba(13,40,24,0.25)]"
              data-testid="hero-tree-image"
            />
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-8 rounded-[100%] bg-[#1B1915]/15 blur-xl" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.3 }}
            className="absolute right-0 top-1/2 -translate-y-1/2 hidden sm:flex flex-col gap-3"
            data-testid="hero-chakra-dots"
          >
            {CHAKRAS.map((c) => (
              <span
                key={c.key}
                className={`w-2 h-2 rounded-full ${c.light ? "border border-[#1B1915]/25" : ""}`}
                style={{ backgroundColor: c.hex }}
                title={c.name}
              />
            ))}
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#chakra-tree"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-3 text-[#1B1915]/45"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] tracking-[0.32em] uppercase">Scroll</span>
        <span className="w-px h-10 bg-[#1B1915]/20 overflow-hidden block">
          <span className="block w-px h-4 bg-[#1B1915]/70 animate-scrollhint" />
        </span>
      </motion.a>
    </section>
  );
}
